// Service Steady PWA — src/components/LocationPickerModal.tsx — v2.0 — built 2026-06-04
//
// Bottom-sheet on mobile, center modal on desktop. Three ways to pick a
// location, in priority order:
//   1. "Use my location" — fires useGeolocation; on success → setLocation
//   2. Search input → Open-Meteo (proxied through Worker, 24h KV cache)
//   3. Recents list (last 3 distinct picks) + a pinned shop default
//
// Empty states:
//   - Search empty: render recents + shop default
//   - Search non-empty but <2 chars: "Type at least 2 characters"
//   - Search loading: "Loading..."
//   - Search no matches: "No matches found"
//
// Hard rule: NO outbound writes. This is read-only UI calling read-only
// Worker endpoints.

import { useEffect, useRef, useState } from 'react';
import { MapPin, Search, X } from 'lucide-react';
import {
  fetchLocationSearch,
  type LocationSearchHit,
} from '@/lib/api/weather';
import {
  useLocation,
  getRecents,
  SHOP_DEFAULT_LOCATION,
  type SsLocation,
} from '@/hooks/useLocation';
import { useGeolocation } from '@/hooks/useGeolocation';

interface Props {
  open: boolean;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;

export function LocationPickerModal({ open, onClose }: Props) {
  const { setLocation } = useLocation();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<LocationSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [recents, setRecents] = useState<SsLocation[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const geo = useGeolocation();
  const [geoToast, setGeoToast] = useState<string | null>(null);

  // Refresh recents every time the sheet opens (in case the user picked
  // somewhere else since last open).
  useEffect(() => {
    if (open) {
      setRecents(getRecents());
      // Auto-focus the search input on open (desktop only; on mobile this
      // also pops the keyboard which is fine since search IS the primary
      // action).
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      // Reset state on close so the next open starts fresh.
      setQuery('');
      setDebounced('');
      setHits([]);
      setSearchError(null);
      setGeoToast(null);
    }
  }, [open]);

  // Debounce search input.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  // Fire search when debounced query changes.
  useEffect(() => {
    let cancelled = false;
    const q = debounced.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    setSearchError(null);
    fetchLocationSearch(q)
      .then((results) => {
        if (cancelled) return;
        setHits(results);
        setSearching(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setSearching(false);
        setSearchError(
          err instanceof Error ? err.message : 'Search unavailable',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const pick = (loc: SsLocation): void => {
    setLocation(loc);
    onClose();
  };

  const onUseLocation = async (): Promise<void> => {
    setGeoToast(null);
    const r = await geo.request();
    if (r) {
      // 2026-06-04 (v3.2) — pass the literal placeholder. The Worker
      // recognizes it via isPlaceholderName() and reverse-geocodes via
      // NWS to return "City, ST". WeatherCard's useEffect then persists
      // the resolved name to localStorage so the next reload shows
      // the real town instantly.
      pick({
        name: 'Current Location',
        lat: r.lat,
        lon: r.lon,
      });
    } else {
      // Display a brief inline error; map error codes to friendlier copy.
      let msg = 'Location unavailable';
      if (geo.error === 'denied') msg = 'Permission denied — enable Location in Settings';
      else if (geo.error === 'timeout') msg = 'Timed out — try again';
      else if (geo.error === 'unsupported') msg = 'Geolocation not supported on this device';
      setGeoToast(msg);
    }
  };

  if (!open) return null;

  const showRecents = debounced.trim().length < 2;
  const showResults = !showRecents;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Choose location"
      onClick={onClose}
    >
      <div
        className="w-full max-h-[85vh] max-w-md overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-[#0F172A] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
          <div className="text-base font-semibold text-ink-900 dark:text-white">
            Choose location
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-50 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Use my location */}
        <div className="px-4 pt-3">
          <button
            type="button"
            onClick={() => void onUseLocation()}
            disabled={geo.status === 'pending'}
            className="flex w-full items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-ink-900 active:bg-ink-50 disabled:opacity-60 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white dark:active:bg-[#1E293B]"
          >
            <MapPin className="h-4 w-4 text-ss-blue" />
            <span>
              {geo.status === 'pending'
                ? 'Getting location…'
                : 'Use my location'}
            </span>
          </button>
          {geoToast ? (
            <div className="mt-1.5 text-xs text-red-600 dark:text-red-400">
              {geoToast}
            </div>
          ) : null}
        </div>

        {/* Search */}
        <div className="px-4 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-[#64748B]" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search town or city"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-9 pr-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ss-blue focus:outline-none dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white dark:placeholder:text-[#64748B]"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Results / recents area */}
        <div className="overflow-y-auto px-2 pb-4 pt-2 sm:max-h-[55vh]">
          {showResults ? (
            <div>
              {searching ? (
                <div className="px-2 py-3 text-sm text-ink-500 dark:text-[#94A3B8]">
                  Loading…
                </div>
              ) : searchError ? (
                <div className="px-2 py-3 text-sm text-red-600 dark:text-red-400">
                  {searchError}
                </div>
              ) : hits.length === 0 ? (
                <div className="px-2 py-3 text-sm text-ink-500 dark:text-[#94A3B8]">
                  {query.trim().length < 2
                    ? 'Type at least 2 characters'
                    : 'No matches found'}
                </div>
              ) : (
                <ul className="divide-y divide-ink-100 dark:divide-[#1F2A37]">
                  {hits.map((hit) => (
                    <li key={`${hit.lat.toFixed(5)},${hit.lon.toFixed(5)}`}>
                      <button
                        type="button"
                        onClick={() =>
                          pick({ name: hit.name, lat: hit.lat, lon: hit.lon })
                        }
                        className="block w-full px-2 py-2.5 text-left active:bg-ink-50 dark:active:bg-[#1E293B]"
                      >
                        <div className="text-sm font-medium text-ink-900 dark:text-white">
                          {hit.name}
                        </div>
                        {hit.county ? (
                          <div className="text-xs text-ink-500 dark:text-[#94A3B8]">
                            {hit.county} County
                          </div>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div>
              {recents.length > 0 ? (
                <div>
                  <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                    Recents
                  </div>
                  <ul className="divide-y divide-ink-100 dark:divide-[#1F2A37]">
                    {recents.map((r) => (
                      <li key={`recent:${r.name}`}>
                        <button
                          type="button"
                          onClick={() => pick(r)}
                          className="block w-full px-2 py-2.5 text-left active:bg-ink-50 dark:active:bg-[#1E293B]"
                        >
                          <div className="text-sm font-medium text-ink-900 dark:text-white">
                            {r.name}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {/* Shop default — always pinned at the bottom as reset
                  affordance. */}
              <div className="border-t border-ink-100 pt-1 dark:border-[#1F2A37]">
                <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                  Shop
                </div>
                <button
                  type="button"
                  onClick={() => pick(SHOP_DEFAULT_LOCATION)}
                  className="block w-full px-2 py-2.5 text-left active:bg-ink-50 dark:active:bg-[#1E293B]"
                >
                  <div className="text-sm font-medium text-ink-900 dark:text-white">
                    {SHOP_DEFAULT_LOCATION.name}{' '}
                    <span className="text-xs font-normal text-ink-500 dark:text-[#94A3B8]">
                      (shop)
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
